import { useEffect, useState } from 'react';
import { useDailyTips } from '@/hooks/useDailyTips';
import { toast } from 'sonner';

const DAILY_TIP_KEY = 'foco_daily_tip_shown';

export function useDailyTipNotification() {
  const { dailyTip, hasHighPriorityTips, isLoadingAi } = useDailyTips();
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    if (hasShown || isLoadingAi) return;

    // Verificar si ya se mostró hoy
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem(DAILY_TIP_KEY);
    
    // Mostrar si es un nuevo día o si hay tips de alta prioridad
    if (lastShown === today && !hasHighPriorityTips) {
      return;
    }

    // Esperar un momento antes de mostrar
    const timer = setTimeout(() => {
      const isAiTip = dailyTip.source === 'ai';
      
      // Fondos más claros y texto de alto contraste para que el tip sea legible.
      // Antes el fondo casi negro (#1a1a2e) + texto con opacity-90 dejaba la
      // descripción prácticamente ilegible.
      const priorityStyles = {
        high: {
          duration: 8000,
          style: isAiTip
            ? { background: '#312e58', color: '#ffffff', borderLeft: '4px solid hsl(14, 90%, 62%)' }
            : { background: '#991b1b', color: '#ffffff', borderLeft: '4px solid #f87171' }
        },
        medium: {
          duration: 6000,
          style: { background: '#92400e', color: '#ffffff', borderLeft: '4px solid #fbbf24' }
        },
        low: {
          duration: 5000,
          style: { background: '#334155', color: '#ffffff', borderLeft: '4px solid hsl(14, 90%, 62%)' }
        },
      };

      const config = priorityStyles[dailyTip.priority];

      toast(`${dailyTip.emoji} ${dailyTip.title}`, {
        description: dailyTip.message,
        duration: config.duration,
        style: config.style,
        // Texto blanco pleno (sin opacity) para máximo contraste sobre el fondo oscuro.
        descriptionClassName: 'text-white',
        action: {
          label: 'Entendido',
          onClick: () => {},
        },
      });

      localStorage.setItem(DAILY_TIP_KEY, today);
      setHasShown(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [dailyTip, hasHighPriorityTips, hasShown, isLoadingAi]);
}

export function DailyTipProvider({ children }: { children: React.ReactNode }) {
  useDailyTipNotification();
  return <>{children}</>;
}
